import React, { useEffect, useState } from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../redux';
import { AuthService } from '../lib/services/auth-service';
import { newOTP, validateOTP, resetPassword } from '../redux/auth/actions';
import {
  ResetPasswordInputs,
  ResetPasswordErrors,
  ValidateOTPErrors,
  NewOTPErrors,
} from '../redux/auth/interfaces';

import logoImg from '../assets/images/kecobo-logo.png';
import backgroundImg from '../assets/images/onboarding-desktop.jpg';
import OTPRequestForm from '../components/forms/auth/otp-request-form';
import OTPValidationForm from '../components/forms/auth/otp-validation-form';
import ResetPasswordForm from '../components/forms/auth/reset-password-form';
import { sendAnalytics } from '../redux/analytics/actions';

const ResetPasswordPage: NextPage = () => {
  const router = useRouter();
  const dispatch = useDispatch();

  const { token } = router.query || {};

  const [userId, setUserId] = useState('');
  const [hasOTP, setHasOTP] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [resetPass, setResetPassword] = useState(false);

  const { isFetching, errors } = useSelector((state: RootState) => state.auth);

  const _requestOTP = (phoneNumber: string) => {
    dispatch(newOTP(phoneNumber, true, false));
  };

  const _validateOTP = (otp: string) => {
    dispatch(validateOTP({ otp, phoneNumber }, { phoneNumber, resetPassword: true }));
  };

  const _resetPassword = (data: ResetPasswordInputs) => {
    dispatch(resetPassword({ ...data, phoneNumber }));
  };

  useEffect(() => {
    if (token) {
      const { resetPassword = false, phoneNumber, userId } = AuthService.decryptVerificationToken(
        token as string,
      );
      setHasOTP(true);
      setUserId(userId);
      setPhoneNumber(phoneNumber);
      setResetPassword(resetPassword);
    }
  }, [token]);

  useEffect(() => {
    dispatch(
      sendAnalytics({
        event: 'resetPasswordPage',
        category: 'Navigation',
        meta: {},
      }),
    );
  }, []);

  return (
    <div className="bg-white">
      <div className="flex h-full">
        <div
          className="flex-1 hidden h-full bg-center bg-cover sm:block"
          style={{ backgroundImage: `url(${backgroundImg})` }}
        />

        <div className="flex flex-col content-center justify-center w-full h-full px-6 py-8 md:w-8/12 lg:w-5/12">
          <div className="w-full max-w-lg mx-auto">
            <div className="w-40 mx-auto lg:w-48">
              <img src={logoImg} alt="" />
            </div>

            <div className="w-full mt-16">
              {!hasOTP && !resetPass && (
                <OTPRequestForm
                  hasOTP={hasOTP}
                  requestOTP={_requestOTP}
                  isGettingOTP={isFetching}
                  errors={errors as NewOTPErrors}
                />
              )}

              {hasOTP && !resetPass && (
                <OTPValidationForm
                  title="Verify Phone Number"
                  phoneNumber={phoneNumber}
                  isValidating={isFetching}
                  resendOTP={_requestOTP}
                  validateOTP={_validateOTP}
                  errors={errors as ValidateOTPErrors}
                />
              )}

              {hasOTP && resetPass && (
                <ResetPasswordForm
                  isResetting={isFetching}
                  resetPassword={_resetPassword}
                  errors={errors as ResetPasswordErrors}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex w-full px-6 py-4 border-t border-gray-400 lg:px-12">
        <p className="flex-1">
          For help contact,{' '}
          <span className="font-semibold ">
            <a href="tel: 0791399159" className="text-indigo-500">
              0791399159
            </a>
            ,{' '}
            <a href="tel: 0756460413" className="text-indigo-500">
              0756460413
            </a>
          </span>{' '}
          or send an email to{' '}
          <span className="font-semibold ">
            <a href="mailto: nrr@copyright.go.ke" className="text-indigo-500">
              nrr@copyright.go.ke
            </a>
          </span>
        </p>
        <p className="flex-0">
          Kenya Copyrights Board,{' '}
          <span className="font-semibold">&#169;{new Date().getFullYear()}</span>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
